
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function Career() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-teal-200 hover:text-white mb-6 transition-colors"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Blog
              </Link>
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                Building a Successful Aviation Career Path
              </h1>
              <div className="flex items-center text-teal-200 text-sm">
                <span>October 30, 2025</span>
                <span className="mx-2">•</span>
                <span>8 min read</span>
                <span className="mx-2">•</span>
                <span>By Stiletto Airpark Team</span>
              </div>
              <p className="text-xl leading-relaxed">
                Explore the various career opportunities in aviation and learn how to build the experience and qualifications needed for your dream flying job in Central Texas and beyond.
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              <img 
                src="https://static.readdy.ai/image/d0668106bc926dd2e77bf53b05b8da4f/4fcd8ef5ee87ab788bc096ed621eafcf.jpeg"
                alt="Aviation career development and pilot progression"
                className="w-full h-64 object-cover rounded-lg mb-8"
              />

              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                Starting Your Aviation Journey in Central Texas
              </h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The aviation industry offers diverse and rewarding career paths, from commercial airline pilots to flight instructors, corporate aviation, and specialized flying roles. At Stiletto Airpark, we've helped countless students in the <strong>Central Texas</strong> region launch successful aviation careers by providing comprehensive <strong>flight training</strong> that builds both technical skills and professional confidence.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Entry-Level Aviation Careers</h3>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Your aviation career typically begins with obtaining your Private Pilot License, followed by building flight hours and experience. Here are common entry points:
              </p>

              <ul className="list-disc pl-6 mb-6 text-gray-700">
                <li><strong>Flight Instructor (CFI):</strong> Teaching others to fly while building your own hours</li>
                <li><strong>Charter Pilot:</strong> Flying small aircraft for charter companies</li>
                <li><strong>Corporate Pilot:</strong> Flying business aircraft for companies</li>
                <li><strong>Agricultural Pilot:</strong> Crop dusting and agricultural aviation</li>
                <li><strong>Pipeline Patrol:</strong> Monitoring infrastructure from the air</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Building Flight Hours and Experience</h3>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                The path to a professional aviation career requires accumulating flight hours and diverse experience. Most commercial opportunities require:
              </p>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h4 className="font-bold text-gray-900 mb-3">Hour Requirements by Career Level:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li><strong>Private Pilot:</strong> 40 hours minimum</li>
                  <li><strong>Commercial Pilot:</strong> 250 hours minimum</li>
                  <li><strong>Airline Transport Pilot:</strong> 1,500 hours minimum</li>
                  <li><strong>Flight Instructor:</strong> Additional training and checkride</li>
                </ul>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Specialized Training Opportunities</h3>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Specialized endorsements and ratings can open unique career opportunities. Our <strong>tailwheel endorsement</strong> training at Stiletto Airpark prepares pilots to fly any aircraft you may encounter.
              </p>

              <img 
                src="https://static.readdy.ai/image/d0668106bc926dd2e77bf53b05b8da4f/fd37867504beb0c6d38e75a216741c7c.jpeg"
                alt="Tailwheel aircraft training for career development"
                className="w-full h-48 object-cover rounded-lg mb-6"
              />

              <h3 className="text-xl font-bold text-gray-900 mb-4">Long-Term Career Planning</h3>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Successful aviation careers require strategic planning and continuous learning. Consider these long-term career paths:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-teal-50 p-6 rounded-lg">
                  <h4 className="font-bold text-teal-800 mb-3">Commercial Aviation</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Regional Airlines</li>
                    <li>• Major Airlines</li>
                    <li>• Cargo Operations</li>
                    <li>• International Routes</li>
                  </ul>
                </div>
                <div className="bg-teal-50 p-6 rounded-lg">
                  <h4 className="font-bold text-teal-800 mb-3">Corporate Aviation</h4>
                  <ul className="text-gray-700 space-y-1">
                    <li>• Business Jets</li>
                    <li>• Charter Operations</li>
                    <li>• Flight Management</li>
                    <li>• Aviation Consulting</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-4">Getting Started at Stiletto Airpark</h3>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                Whether you're beginning your aviation journey or advancing your career, Stiletto Airpark provides the professional training and mentorship needed for success. Our experienced instructors have diverse aviation backgrounds and can provide valuable career guidance throughout your training.
              </p>

              <div className="bg-teal-600 text-white p-8 rounded-lg text-center">
                <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  Ready to Start Your Aviation Career?
                </h4>
                <p className="text-lg mb-6">
                  Contact our experienced instructors to discuss your career goals and training options.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="tel:706-936-8961" 
                    className="bg-white text-teal-600 px-6 py-3 rounded-md font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Call Bryson: 706-936-8961
                  </a>
                  <a 
                    href="tel:512-658-3132" 
                    className="border-2 border-white text-white px-6 py-3 rounded-md font-semibold hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Call Scott: 512-658-3132
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Barlow, sans-serif' }}>
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <article className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=flight%20instructor%20teaching%20student%20pilot%20in%20aircraft%20cockpit%2C%20professional%20aviation%20training%20tips%2C%20flight%20training%20education%20scene&width=400&height=200&seq=training-tips-related&orientation=landscape"
                  alt="Flight training tips"
                  className="w-full h-32 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    <Link to="/blog/training-tips" className="hover:text-teal-600 transition-colors">
                      Essential Flight Training Tips for New Pilots
                    </Link>
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Discover proven strategies that will accelerate your flight training progress.
                  </p>
                </div>
              </article>
              
              <article className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=texas%20weather%20patterns%20from%20pilot%20perspective%2C%20aviation%20weather%20analysis%2C%20clear%20skies%20over%20central%20texas&width=400&height=200&seq=weather-related&orientation=landscape"
                  alt="Texas weather patterns"
                  className="w-full h-32 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    <Link to="/blog/weather" className="hover:text-teal-600 transition-colors">
                      Understanding Texas Weather Patterns for Pilots
                    </Link>
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Learn to read Central Texas weather conditions for safe flying decisions.
                  </p>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
